/**
 * ShowsListPage - Admin list view of all shows
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getShows, formatCurrency, type Show } from '../../../api/admin';

export default function ShowsListPage() {
  const [shows, setShows] = useState<Show[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadShows();
  }, []);

  const loadShows = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getShows();
      setShows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shows');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredShows = shows.filter((show) => {
    if (filter === 'active') return show.is_active;
    if (filter === 'inactive') return !show.is_active;
    return true;
  });

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-warm-100 rounded-lg" />
                <div className="flex-1">
                  <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-warm-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <p className="text-danger-600 mb-4">{error}</p>
        <button onClick={loadShows} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Shows</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage Broadway shows and their class options.
          </p>
        </div>
        <Link to="/admin/shows/new" className="btn-primary min-h-[44px] flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">Add </span>Show
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
          }`}
        >
          All ({shows.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'active'
              ? 'bg-primary-600 text-white'
              : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
          }`}
        >
          Active ({shows.filter((s) => s.is_active).length})
        </button>
        <button
          onClick={() => setFilter('inactive')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'inactive'
              ? 'bg-primary-600 text-white'
              : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
          }`}
        >
          Inactive ({shows.filter((s) => !s.is_active).length})
        </button>
      </div>

      {/* Shows List */}
      {filteredShows.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">
            {filter === 'all' ? 'No shows yet' : `No ${filter} shows`}
          </h3>
          <p className="text-warm-600 mb-6">
            {filter === 'all'
              ? 'Create your first Broadway show to get started.'
              : `There are no ${filter} shows at the moment.`}
          </p>
          {filter === 'all' && (
            <Link to="/admin/shows/new" className="btn-primary">
              Add Your First Show
            </Link>
          )}
        </div>
      ) : (
        <>
        {/* Desktop table */}
        <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-warm-50 border-b border-warm-100">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                  Show
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                  Default Price
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                  Tour Stops
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                  Status
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-100">
              {filteredShows.map((show) => (
                <tr key={show.id} className="hover:bg-warm-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      {show.logo_url ? (
                        <img
                          src={show.logo_url}
                          alt={show.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-bold text-lg">
                            {show.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-warm-900">{show.name}</p>
                        <p className="text-sm text-warm-500">{show.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-warm-900">
                      {formatCurrency(show.default_base_price_cents)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-warm-600">
                      {show.tour_stops?.[0]?.count || 0} stops
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        show.is_active
                          ? 'bg-success-100 text-success-700'
                          : 'bg-warm-100 text-warm-600'
                      }`}
                    >
                      {show.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/shows/${show.id}/edit`}
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        Edit
                      </Link>
                      <Link
                        to={`/admin/tour-stops?show=${show.id}`}
                        className="px-3 py-1.5 text-sm text-warm-600 hover:text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
                      >
                        Tour Stops
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {filteredShows.map((show) => (
            <Link
              to={`/admin/shows/${show.id}/edit`}
              key={show.id}
              className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
            >
              <div className="flex items-center gap-3 mb-2">
                {show.logo_url ? (
                  <img
                    src={show.logo_url}
                    alt={show.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-bold text-lg">
                      {show.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-warm-900 truncate">{show.name}</p>
                  <p className="text-sm text-warm-500">
                    {formatCurrency(show.default_base_price_cents)}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-warm-600">
                  {show.tour_stops?.[0]?.count || 0} tour stops
                </span>
                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                    show.is_active
                      ? 'bg-success-100 text-success-700'
                      : 'bg-warm-100 text-warm-600'
                  }`}
                >
                  {show.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </Link>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
